<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DOMPurify from 'dompurify';

const props = defineProps({
  htmlSignature: { type: String, default: '' },
  plainSignature: { type: String, default: '' },
  enabled: { type: Boolean, default: false },
  editorId: { type: [Number, String], default: null },
  allowToggleSignature: { type: Boolean, default: true },
});

const emit = defineEmits(['insertSignature']);

const { t } = useI18n();

const isExpanded = ref(false);

watch(
  () => props.editorId,
  () => {
    isExpanded.value = false;
  }
);

const extractedText = computed(() => {
  if (props.plainSignature) {
    const lines = props.plainSignature
      .split('\n')
      .filter(line => line.trim().length > 0);
    return lines.slice(0, 2).join('\n');
  }

  if (!props.htmlSignature) return null;

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = DOMPurify.sanitize(props.htmlSignature);
  const textContent = tempDiv.textContent || tempDiv.innerText || '';

  const lines = textContent.split('\n').filter(line => line.trim().length > 0);

  if (lines.length === 0) {
    const hasImage = tempDiv.querySelector('img') !== null;

    return hasImage ? t('CONVERSATION.FOOTER.SIGNATURE_PREVIEW_IMAGE') : null;
  }

  return lines.slice(0, 2).join('\n');
});

const shouldShow = computed(() => {
  if (!props.enabled) return false;
  if (!props.htmlSignature && !props.plainSignature) return false;
  if (isExpanded.value) return true;

  return extractedText.value;
});

const sanitizedHtmlSignature = computed(() => {
  return DOMPurify.sanitize(props.htmlSignature);
});

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

const handleInsertSignature = event => {
  event.stopPropagation();
  const signature = props.htmlSignature || props.plainSignature;
  emit('insertSignature', signature);
  isExpanded.value = false;
};
</script>

<template>
  <div
    v-if="shouldShow"
    class="signature-preview cursor-pointer relative"
    @click="toggleExpanded"
  >
    <template v-if="isExpanded">
      <div class="relative pt-1">
        <button
          v-if="allowToggleSignature"
          v-tooltip.left="t('CONVERSATION.FOOTER.INSERT_SIGNATURE')"
          class="absolute top-2 right-0 p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors z-10"
          @click="handleInsertSignature"
        >
          <fluent-icon icon="pencil" size="16" icon-lib="tabler" />
        </button>
        <div v-if="props.htmlSignature" v-html="sanitizedHtmlSignature" />
        <div v-else class="whitespace-pre-line border-t border-n-slate-8/20">
          {{ plainSignature }}
        </div>
      </div>
    </template>
    <div
      v-else
      class="text-xs text-slate-500 dark:text-slate-400 whitespace-pre-line border-t border-n-slate-8/20"
    >
      {{ extractedText }}
    </div>
  </div>
</template>
